/**
 * Reusable stateless form component for ContentModule
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// import third-party libraries
import { EditorState, convertToRaw } from 'draft-js';

// import form components
import {
  NumberInput
  , RadioInput
  // , RichEditor
  , SelectFromArray
  , SelectFromObject
  // , SimpleArrayEditor
  , TextAreaInput
  , TextInput
  , UrlInput
  , CdnPathInput
  , CheckboxInput
} from '../../../global/components/forms';

function ContentModuleForm({
  cancelLink
  , certificateTemplates
  , formHelpers
  , formTitle
  , formType
  , handleFormChange
  , handleFormSubmit
  , sponsorStore
  , studyStore
  , contentModule
  , hasActiveEvals
}) {
  // console.log(certificateTemplates)
  // set the button text
  const buttonText = formType === "create" ? "Create Content Module" : "Update Content Module";

  // set the form header
  const header = formTitle ? <div className="formHeader"><h2> {formTitle} </h2><hr /></div> : <div />;

  // SelectFromObject arrays
  let sponsorsArray = sponsorStore ? sponsorStore.util.getList() : [];
  sponsorsArray = sponsorsArray && sponsorsArray.length > 0 ? sponsorsArray : [];
  let studiesArray = studyStore ? studyStore.util.getList("_organization", contentModule._organization) : [];
  studiesArray = studiesArray && studiesArray.length > 0 ? studiesArray : [];
  // console.log(contentModule._organization);

  return (
    <div className="yt-container">
      <div className="yt-row center-horiz">
        <div className="form-container -slim">
          <form name="contentModuleForm" className="contentModule-form" onSubmit={handleFormSubmit}>
            {header}
            <div className="-form-step"><span className="-num">1</span> General info</div>
            <div className="yt-col full m_60">
              <TextInput
                change={handleFormChange}
                label="Title"
                name="contentModule.title"
                required={true}
                value={contentModule.title}
              />
              <TextInput
                change={handleFormChange}
                helpText={<span><strong>NOTE: </strong> must be unique.<br />This is to help reduce the length of the URL (i.e. 'improving-non-hodgkin-lymphoma')</span>}
                label="Cononical URL Slug"
                name="contentModule.slug"
                placeholder="example-content-module"
                required={true}
                value={contentModule.slug}
              />
              <TextInput
                change={handleFormChange}
                helpText={<span><strong>NOTE: </strong> can be anything, but ACRP expects this to be a date like "June 12, 2020"</span>}
                label="Version"
                name="contentModule.version"
                required={false}
                value={contentModule.version}
              />
              <SelectFromArray
                change={handleFormChange}
                display={'type'}
                disabled={true}
                helpText={<span><strong>NOTE: </strong> only one possible type. placeholder for future customization options. </span>}
                label="Type"
                name="contentModule.type"
                placeholder="-- Select a type --"
                items={['contentModule']}
                required={true}
                value={contentModule.type}
              />
              <TextAreaInput
                change={handleFormChange}
                helpText={<span>{contentModule.summary ? contentModule.summary.length : 0} of 250</span>}
                label="Brief summary"
                maxLength="250"
                name="contentModule.summary"
                required={false}
                rows="3"
                value={contentModule.summary}
              />
              <UrlInput
                change={handleFormChange}
                label="Brand Logo"
                helpText="This will show up on the agenda page and take priority over sponsor logo. Otherwise will default to Proficiency logo."
                name="contentModule.brandingLogoUrl"
                placeholder="http://..."
                required={false}
                value={contentModule.brandingLogoUrl}
              />
              <CheckboxInput
                change={handleFormChange}
                checked={contentModule.allowSimFeedback}
                helpText={<span>Open to feedback from approved users.<br /> <strong>NOTE: </strong> Module can not be added to a course while feedback is active.</span>}
                label='Allow Sim Feedback'
                name='contentModule.allowSimFeedback'
                value={contentModule.allowSimFeedback}
              />
              {/* <CheckboxInput // this isn't necessary, messaging is handled above. May add back later if/when feedback on published modules is allowed.
                change={() => { }}
                checked={contentModule.published}
                label='Published'
                // disabled={true}
                helpText={
                  contentModule.published ?
                    'Module can be added to courses.'
                    : 'Module can NOT be added to courses until feedback is reviewed and module is published.'
                }
                name='contentModule.published'
                value={contentModule.published}
              /> */}
              <CheckboxInput
                change={handleFormChange}
                checked={contentModule.allowAnonymous}
                helpText={
                  hasActiveEvals ?
                  'Module has active evaluations and cannot allow anonymous access.'
                  : 'Module can be taken without user tracking (Embeded in external site, etc).'
                }
                label='Allow Anonymous Access'
                name='contentModule.allowAnonymous'
                value={contentModule.allowAnonymous}
                disabled={hasActiveEvals ? true : false}
              />
              <CheckboxInput
                change={handleFormChange}
                checked={contentModule.allowCompletionRedirect}
                label='Allow Redirect on Completion'
                helpText='Module can redirect to a custom URL (defined on the group) upon completion.'
                name='contentModule.allowCompletionRedirect'
                value={contentModule.allowCompletionRedirect}
              />
            </div>
            <div className="-form-step"><span className="-num">2</span> Media</div>
            <div className="yt-col full m_60">
              <CdnPathInput
                change={handleFormChange}
                label="Trailer Video Path"
                name="contentModule.trailerVidPath"
                required={false}
                validate={false}
                value={contentModule.trailerVidPath}
              />
              <CdnPathInput
                change={handleFormChange}
                label="Trailer Poster Path"
                name="contentModule.trailerPosterPath"
                required={false}
                validate={false}
                value={contentModule.trailerPosterPath}
              />
            </div>

            <div className="-form-step"><span className="-num">3</span> Sponsor/study</div>
            <div className="yt-col full m_60">
              <SelectFromObject
                change={handleFormChange}
                display={'name'}
                filterable={true}
                helpText={<span><strong>NOTE: </strong> if left empty this will be available for ALL sponsors. <br /><strong>IMPORTANT:</strong> Content modules that are not tied to a specific sponsor/study cannot be excluded via role-based training.</span>}
                label="Sponsor (optional) "
                name="contentModule._organization"
                objects={sponsorsArray}
                placeholder="-- Select a Sponsor --"
                required={false}
                selected={contentModule._organization}
                value={'_id'}
              />
              {contentModule._organization && contentModule._organization.length > 0 ?
                <SelectFromObject
                  change={handleFormChange}
                  display={'name'}
                  filterable={true}
                  helpText={<span><strong>NOTE: </strong> if left empty this will be available for ALL studies for this sponsor.</span>}
                  label="Study (optional)"
                  name="contentModule._group"
                  objects={studiesArray}
                  placeholder="-- Select a Study --"
                  required={false}
                  selected={contentModule._group}
                  value={'_id'}
                />
                :
                null
              }
            </div>
            <div className="-form-step"><span className="-num">4</span> Conclusion</div>
            <div className="yt-col full m_60">
              <CdnPathInput
                change={handleFormChange}
                label="Conclusion Video Path"
                name="contentModule.conclusionVidPath"
                required={false}
                validate={false}
                value={contentModule.conclusionVidPath}
              />
              <CdnPathInput
                change={handleFormChange}
                label="Conclusion Poster Path"
                name="contentModule.conclusionPosterPath"
                required={false}
                validate={false}
                value={contentModule.conclusionPosterPath}
              />
            </div>
            <RadioInput
              helpText={
                formHelpers.isTestable === "yes" && contentModule._evaluation ?
                  <Link to={`/admin/evaluations/${contentModule._evaluation}/update`} target="_blank" className="yt-btn small link">Manage Evaluation <i className="fa fa-external-link" /></Link>
                  : formHelpers.isTestable === "yes" ?
                    <span><strong>NOTE: </strong> You can build and manage the evaluation for this module from the next screen.</span>
                    :
                    <span>User will <strong>only</strong> see the conclusion page.</span>
              }
              label="Is this content module testable?"
              options={[
                { val: 'yes', display: 'Yes' },
                { val: 'no', display: 'No' },
              ]}
              name="formHelpers.isTestable"
              value={formHelpers.isTestable}
              change={handleFormChange}
              inLine={true}
              disabled={contentModule.allowAnonymous}
            />
            <RadioInput
              helpText={
                formHelpers.requiresAttestation === "yes" ?
                  <span>Upon completing the training, users must go through an additional "attestation" step before receiving a completed certificate.</span>
                  :
                  <span>Users will receive a completed certificate upon completion the training. <strong>NOTE:</strong> This is the default.</span>
              }
              label="Does this training require additional user attestation?"
              options={[
                { val: 'yes', display: 'Yes' },
                { val: 'no', display: 'No' },
              ]}
              name="formHelpers.requiresAttestation"
              value={formHelpers.requiresAttestation}
              change={handleFormChange}
              inLine={true}
              disabled={contentModule.allowAnonymous}
            />
            <CheckboxInput
              change={handleFormChange}
              checked={formHelpers.showSentimentModal}
              helpText='Will show sentiment question display at the end of the module'
              label='Show sentiment modal on conclusion'
              name='formHelpers.showSentimentModal'
              value={formHelpers.showSentimentModal}
            />
            <div id="certs" className="-form-step"><span className="-num">5</span> Manage Certificates of Completion</div>
            {contentModule.allowAnonymous ?
              <div style={{ paddingTop: 20, paddingBottom: 20 }}>
                <span><strong>NOTE: </strong>This module allows Anonymous access. Any users taking this training anonymously will NOT be awarded a Certificate and will NOT be tracked in the system.</span>
              </div>
              : null
            }
            <RadioInput
              helpText={
                formHelpers.doesCertExpire === "yes" ?
                  <span><strong>NOTE: </strong> Set the duration (in years) of the certificate's validity.</span>
                  :
                  <span>Certificate of completion will <strong> NEVER </strong> expire.</span>
              }
              label="Does a user's certificate of completion expire?"
              options={[
                { val: 'yes', display: 'Yes' },
                { val: 'no', display: 'No' },
              ]}
              name="formHelpers.doesCertExpire"
              value={formHelpers.doesCertExpire}
              change={handleFormChange}
              inLine={true}
            />
            {formHelpers.doesCertExpire === 'yes' ?
              <div className="yt-col full m_30">
                <NumberInput
                  change={handleFormChange}
                  helpText={<span><strong>EXTRA DOUBLE NOTE:</strong> Duration is in YEARS</span>}
                  label="Number of years the certificate is valid"
                  min="0"
                  name="contentModule.certificateDuration"
                  step="0.5"
                  value={contentModule.certificateDuration}
                />
              </div>
              :
              null
            }
            <div className="yt-col full m_60">
              <SelectFromArray
                change={handleFormChange}
                display={'certificateType'}
                helpText={<span><strong>NOTE: </strong> this determines the 3rd party output when a certificate is created, if those options are selected.</span>}
                label="Certificate Type"
                name="contentModule.certificateType"
                placeholder="-- Select a type --"
                items={['default', 'acrp', 'cme', 'cne']}
                required={true}
                value={contentModule.certificateType}
              />
              <NumberInput
                change={handleFormChange}
                // helpText={<span><strong>NOTE:</strong> Duration is in YEARS</span>}
                label="Number of credits for this module (optional)"
                min="0"
                name="contentModule.certificateUnits"
                step="0.1"
                value={contentModule.certificateUnits}
              />
              <TextInput
                change={handleFormChange}
                helpText={<span><strong>NOTE: </strong> this is highly specfic to ACRP modules until we know what it actually means. for now, it is a custom field defined by Bridgett, according to the docs they provided.</span>}
                label="ACRP PPR NUMBER"
                name="contentModule.acrp_ppr_number"
                placeholder="000-2019-ACE-103"
                required={false}
                value={contentModule.acrp_ppr_number}
              />
              <SelectFromObject
                change={handleFormChange}
                display={'name'}
                filterable={true}
                helpText={<span><strong>NOTE: </strong> This is optional, if selected all certificates for this module will be built with this template. Otherwise, certificate will be standard Pro-ficiency cert.</span>}
                label="Certificate Template"
                name="contentModule._certificateTemplate"
                objects={certificateTemplates}
                placeholder="-- Select a Template--"
                required={false}
                selected={contentModule._certificateTemplate}
                value={'_id'}
              />
            </div>
            <div className="input-group">
              <div className="yt-row space-between">
                <Link className="yt-btn link" to={cancelLink}>Cancel</Link>
                <button className="yt-btn " type="submit" > {buttonText} </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

ContentModuleForm.propTypes = {
  cancelLink: PropTypes.string.isRequired
  , certificateTemplates: PropTypes.array
  , formHelpers: PropTypes.object
  , formTitle: PropTypes.string
  , formType: PropTypes.string.isRequired
  , handleFormChange: PropTypes.func.isRequired
  , handleFormSubmit: PropTypes.func.isRequired
  , sponsorStore: PropTypes.object.isRequired
  , studyStore: PropTypes.object.isRequired
  , contentModule: PropTypes.object.isRequired
}

ContentModuleForm.defaultProps = {
  certificateTemplates: []
  , formHelpers: {}
  , formTitle: ''
}

export default ContentModuleForm;



// WEBPACK FOOTER //
// modules/contentModule/components/ContentModuleForm.js.jsx